/**
 * @biomax/plugin-mobility — inter-region mobility models.
 *
 * v0.1 scope:
 *   - Gravity model: T_{ij} = k · (N_i · N_j) / d_{ij}^β
 *   - Radiation model (Simini 2012): parameter-free, uses
 *     intervening-opportunities formulation
 *   - Per-region OD-matrix builder from region populations + distances
 *
 * Used by the v0.3 metapopulation simulator to weight cross-region
 * transmission edges.
 */

import type { PluginManifest } from '@biomax/types';

export interface MobilityRegion {
  id: string;
  population: number;
  /** Centroid [lat, lon] in degrees. */
  centroidLatLon: [number, number];
}

/** Great-circle distance in km via haversine. */
export function haversineKm(a: [number, number], b: [number, number]): number {
  const R = 6371;
  const φ1 = (a[0] * Math.PI) / 180;
  const φ2 = (b[0] * Math.PI) / 180;
  const dφ = ((b[0] - a[0]) * Math.PI) / 180;
  const dλ = ((b[1] - a[1]) * Math.PI) / 180;
  const x = Math.sin(dφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(dλ / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

export interface GravityModelParams {
  /** Distance-decay exponent β. Higher = more local. */
  beta: number;
  /** Normalisation constant. */
  k: number;
}

/**
 * Build an origin-destination matrix via the gravity model.
 * T[i][j] = k · (N_i · N_j) / d_{ij}^β  (T[i][i] = 0)
 */
export function gravityODMatrix(
  regions: MobilityRegion[], params: GravityModelParams,
): number[][] {
  const M = regions.length;
  const T: number[][] = Array.from({ length: M }, () => new Array(M).fill(0));
  for (let i = 0; i < M; i++) {
    for (let j = 0; j < M; j++) {
      if (i === j) continue;
      const d = haversineKm(regions[i]!.centroidLatLon, regions[j]!.centroidLatLon);
      if (d <= 0) continue;
      T[i]![j] = params.k * (regions[i]!.population * regions[j]!.population) / Math.pow(d, params.beta);
    }
  }
  return T;
}

/**
 * Build an OD matrix via the parameter-free radiation model
 * (Simini et al. 2012 Nature). For each origin i, destinations are
 * ranked by distance; flow uses the intervening-opportunities idea.
 *
 *   T_ij = T_i · (n_i · n_j) / ((n_i + s_ij)(n_i + n_j + s_ij))
 *
 * where s_ij = total population in the circle around i with radius d_ij,
 * excluding i and j themselves.
 *
 * T_i is total outflow from i (commonly set to a constant fraction of N_i).
 */
export function radiationODMatrix(
  regions: MobilityRegion[],
  /** Fraction of N_i that commutes/migrates. */
  totalOutflowFraction: number,
): number[][] {
  const M = regions.length;
  // Pre-compute distances.
  const D: number[][] = Array.from({ length: M }, () => new Array(M).fill(0));
  for (let i = 0; i < M; i++) {
    for (let j = 0; j < M; j++) {
      D[i]![j] = haversineKm(regions[i]!.centroidLatLon, regions[j]!.centroidLatLon);
    }
  }
  const T: number[][] = Array.from({ length: M }, () => new Array(M).fill(0));
  for (let i = 0; i < M; i++) {
    const Ti = regions[i]!.population * totalOutflowFraction;
    const ni = regions[i]!.population;
    for (let j = 0; j < M; j++) {
      if (i === j) continue;
      const nj = regions[j]!.population;
      // Sum populations within radius D[i][j] of i, excluding i + j.
      let sij = 0;
      for (let k = 0; k < M; k++) {
        if (k === i || k === j) continue;
        if (D[i]![k]! < D[i]![j]!) sij += regions[k]!.population;
      }
      const denom = (ni + sij) * (ni + nj + sij);
      T[i]![j] = denom > 0 ? (Ti * ni * nj) / denom : 0;
    }
  }
  return T;
}

/**
 * Citations the plugin's models are drawn from. Each PMID has been
 * round-tripped via @biomax/plugin-literature-fetch (verifiedAt date
 * recorded). To re-verify, run scripts/verify-references.ts.
 */
export const MOBILITY_REFERENCES = [
  {
    id: 'gravity-newton',
    label: 'Gravity model — canonical form predates modern epi use',
    pmid: null,
    doi: null,
    note: 'The gravity-form T_ij = k · N_i · N_j / d_ij^β has no single PMID. Modern epi calibration: see tizzoni-2014.',
    verifiedAt: null,
  },
  {
    id: 'tizzoni-2014',
    label: 'Tizzoni et al. 2014 PLoS Comput Biol — "On the use of human mobility proxies for modeling epidemics"',
    pmid: '25010676',
    doi: '10.1371/journal.pcbi.1003716',
    note: 'Empirical β calibration from European census + mobile-phone data. Provides the v0.1 default β=2 in gravityODMatrix.',
    verifiedAt: '2026-05-28',
  },
  {
    id: 'simini-2012',
    label: 'Simini et al. 2012 Nature — "A universal model for mobility and migration patterns"',
    pmid: '22367540',
    doi: '10.1038/nature10856',
    note: 'Parameter-free intervening-opportunities formulation. Implemented in radiationODMatrix().',
    verifiedAt: '2026-05-28',
  },
  {
    id: 'balcan-vespignani-2010',
    label: 'Balcan & Vespignani 2010 J Comput Sci — "Modeling the spatial spread of infectious diseases: GLEAM"',
    pmid: '21415939',
    doi: '10.1016/j.jocs.2010.07.002',
    note: 'Foundational pattern for metapopulation epi via mobility coupling. simulateMetapopulation in @biomax/plugin-epidemiology follows this template.',
    verifiedAt: '2026-05-28',
  },
] as const;

export const manifest: PluginManifest = {
  id: 'mobility', version: '0.1.0',
  engineCompat: '^0.5.0', kingdoms: '*', systems: [], components: [],
};
